import { Button, Stack } from '@mui/material'
import xor from 'lodash/xor'
import { useCallback, useEffect, useState } from 'react'
import { Fade, IconButton } from 'utoniq-ui'

import {
  ConsolePreSaleItemSelectBoxFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BottomSafeArea } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  FullscreenModal,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'

import { ItemSelectBox, ItemSelectBoxSkeleton } from './ItemSelectBox'
import { ItemSelectForm, ItemSelectFormProps } from './ItemSelectForm'

export type ItemSelectModalProps = {
  artistId: string
  open: boolean
  onClose: () => void
  loading: boolean
  hasNextPage: boolean
  addingItem: boolean
  values: string[]
  onFetchMore: () => Promise<void>
  items: Maybe<{ edges: { node: ConsolePreSaleItemSelectBoxFragment }[] }>
  onSearch: ItemSelectFormProps['onSubmit']
  onSelect: (items: string[]) => Promise<void>
}

export const ItemSelectModal: Organism<ItemSelectModalProps> = ({
  artistId,
  open,
  values,
  onClose,
  loading,
  hasNextPage,
  addingItem,
  onFetchMore,
  items,
  onSearch,
  onSelect,
}) => {
  const { t } = useTranslation('consolePreSaleDetail')
  const { showError } = useMessage()
  const [itemSelected, setItemSelected] = useState<string[]>([])

  /**
   * @HACK
   * フィルタリング後のアイテム表示数が10件に満たない場合、アイテム全てをクエリ取得する or 10件表示するまで自動でrefetchする。
   */
  useEffect(() => {
    if (
      !loading &&
      hasNextPage &&
      items?.edges?.length !== undefined &&
      items?.edges?.length < 10
    ) {
      onFetchMore()
    }
  }, [loading, hasNextPage])

  useEffect(() => {
    setItemSelected(values)
  }, [values])

  const addItemToCollection = useCallback(async () => {
    return onSelect(itemSelected)
      .then(() => {
        onClose()
      })
      .catch(e => {
        showError(e)
      })
  }, [itemSelected, onClose, onSelect, showError])

  const handleSelectItem = (itemId: string) => {
    if (!items) return
    const item = items.edges.find(element => element.node.id === itemId)
    if (
      item &&
      (item.node.paymentType !== 'cash' || item.node.type !== 'ticket')
    ) {
      showError(t('notAllowItemSelect'))
    } else {
      setItemSelected(prevState => xor(prevState, [itemId]))
    }
  }

  return (
    <FullscreenModal open={open}>
      <Fade>
        <ArtistThemeProvider artistId={artistId}>
          <ConsoleBaseLayout
            customNavigation={
              itemSelected.length ? (
                <FixedBottomCell
                  onSelect={() => addItemToCollection()}
                  loading={addingItem}
                  number={itemSelected.length}
                />
              ) : null
            }
            margin
            header={{
              left: (
                <IconButton.Back
                  edge="start"
                  size="medium"
                  color="white"
                  onClick={() => {
                    onClose()
                    setItemSelected(values)
                  }}
                />
              ),
              title: t('itemSelection'),
            }}
          >
            <ItemSelectForm onSubmit={onSearch} />
            <ScrollFetcher
              nodes={items?.edges?.map(({ node }) => (
                <ItemSelectBox
                  key={node.id}
                  item={node}
                  checked={itemSelected.includes(node.id)}
                  onSelect={itemId => handleSelectItem(itemId)}
                />
              ))}
              emptyMessage={t('emptyItemSelect')}
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMore}
              loadingComponent={
                <Stack spacing={5} sx={{ pb: 2 }}>
                  {Array.from(Array(4).keys()).map(key => (
                    <ItemSelectBoxSkeleton key={key} />
                  ))}
                </Stack>
              }
            />
          </ConsoleBaseLayout>
        </ArtistThemeProvider>
      </Fade>
    </FullscreenModal>
  )
}

type FixedBottomCellProps = {
  onSelect: () => void
  loading: boolean
  number: number
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  onSelect,
  loading,
  number,
}) => {
  const { t } = useTranslation('consolePreSaleDetail')
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame sx={{ py: 4, bgcolor: 'grey.50', textAlign: 'center' }}>
        <Button
          size="large"
          sx={{ minWidth: 244 }}
          onClick={onSelect}
          loading={loading}
          disabled={loading}
        >
          {t('addSelectedItem', { number })}
        </Button>
      </CellFrame>
    </BottomSafeArea>
  )
}
